import { useState, useEffect, useCallback, useRef } from "react";
import { useApp } from "../context/AppContext";

const STATUS_MAP = {
  open: { label: "Відкритий", color: "#3b82f6", bg: "rgba(59,130,246,0.1)", icon: "🔵" },
  in_progress: { label: "В роботі", color: "#f59e0b", bg: "rgba(245,158,11,0.1)", icon: "🟡" },
  resolved: { label: "Вирішено", color: "#10b981", bg: "rgba(16,185,129,0.1)", icon: "🟢" },
  closed: { label: "Закритий", color: "#6b7280", bg: "rgba(107,114,128,0.1)", icon: "⚫" },
};
const PRIORITY_MAP = {
  low: { label: "Low", color: "#6b7280" }, medium: { label: "Medium", color: "#f59e0b" },
  high: { label: "High", color: "#ef4444" }, urgent: { label: "Urgent", color: "#dc2626" },
};

export default function MyTicketsView() {
  const { apiFetch, flash, dark, t, card, inp, btnP, btnG, btnSm, currentUser } = useApp();
  const [tickets, setTickets] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openTicket, setOpenTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [filterTab, setFilterTab] = useState("assigned"); // assigned | created | all
  const [filterStatus, setFilterStatus] = useState("");
  const commentsEndRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [tR, uR] = await Promise.all([
        apiFetch("/api/tickets").then(r => r.json()),
        apiFetch("/api/kanban/users").then(r => r.json()),
      ]);
      if (Array.isArray(tR)) setTickets(tR);
      if (Array.isArray(uR)) setUsers(uR);
    } catch {}
    setLoading(false);
  }, [apiFetch]);

  useEffect(() => { load(); }, [load]);

  const myUser = users.find(u => u.username === currentUser);
  const myUserId = myUser?.id;

  const updateTicket = async (id, updates) => {
    try {
      const r = await apiFetch(`/api/tickets/${id}`, { method: "PUT", body: JSON.stringify(updates) });
      const d = await r.json();
      if (d.id) { setTickets(prev => prev.map(tk => tk.id === d.id ? d : tk)); if (openTicket?.id === d.id) setOpenTicket(d); }
    } catch {}
  };

  const loadComments = async (id) => {
    try { const r = await apiFetch(`/api/tickets/${id}/comments`); const d = await r.json(); if (Array.isArray(d)) setComments(d); } catch {}
  };

  const addComment = async () => {
    if (!commentText.trim() || !openTicket) return;
    try {
      const r = await apiFetch(`/api/tickets/${openTicket.id}/comments`, { method: "POST", body: JSON.stringify({ text: commentText.trim() }) });
      const d = await r.json();
      if (d.id) { setComments(prev => [...prev, d]); setCommentText(""); }
      setTimeout(() => commentsEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch {}
  };

  const openModal = (tk) => { setOpenTicket(tk); setComments([]); setCommentText(""); loadComments(tk.id); };
  const getUserName = (tk, field) => tk[field + "_full_name"] || tk[field + "_name"]?.split("@")[0] || "";
  const ago = (d) => { if (!d) return ""; const m = Math.floor((Date.now() - new Date(d).getTime()) / 60000); if (m < 60) return `${m}хв`; const h = Math.floor(m / 60); if (h < 24) return `${h}год`; return `${Math.floor(h / 24)}д`; };

  const filtered = tickets.filter(tk => {
    if (filterTab === "assigned" && tk.assignee_id !== myUserId) return false;
    if (filterTab === "created" && tk.author_id !== myUserId) return false;
    if (filterStatus && tk.status !== filterStatus) return false;
    return true;
  });

  const assignedCount = tickets.filter(tk => tk.assignee_id === myUserId && tk.status !== "closed").length;
  const createdCount = tickets.filter(tk => tk.author_id === myUserId).length;

  if (loading) return <div style={{ textAlign: "center", padding: 40, color: t.textMuted }}>Завантаження...</div>;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>🎫 Мої тікети</h2>
        <div style={{ display: "flex", gap: 4 }}>
          {[
            { key: "assigned", label: `Мені (${assignedCount})`, color: "#6366f1" },
            { key: "created", label: `Створені (${createdCount})`, color: "#8b5cf6" },
            { key: "all", label: `Всі (${tickets.length})`, color: "#6b7280" },
          ].map(tab => (
            <button key={tab.key} onClick={() => setFilterTab(tab.key)} style={{
              ...btnSm, fontSize: 11, padding: "4px 12px",
              background: filterTab === tab.key ? tab.color : "transparent",
              color: filterTab === tab.key ? "#fff" : t.textMuted,
              border: `1px solid ${filterTab === tab.key ? tab.color : t.border}`,
            }}>{tab.label}</button>
          ))}
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...inp, width: 130, fontSize: 11, padding: "4px 8px", marginLeft: "auto" }}>
          <option value="">Всі статуси</option>
          {Object.entries(STATUS_MAP).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
        </select>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {filtered.map(tk => {
          const st = STATUS_MAP[tk.status] || STATUS_MAP.open;
          const pr = PRIORITY_MAP[tk.priority] || PRIORITY_MAP.medium;
          return (
            <div key={tk.id} onClick={() => openModal(tk)} style={{
              ...card, padding: "12px 16px", margin: 0, cursor: "pointer", display: "flex", alignItems: "center", gap: 12,
              borderLeft: `4px solid ${st.color}`,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{tk.title}</span>
                  {tk.is_client && <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 4, background: "rgba(168,85,247,0.1)", color: "#a855f7" }}>КЛІЄНТ</span>}
                  {tk.client_approval === "approved" && <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 4, background: "rgba(16,185,129,0.15)", color: "#10b981" }}>✅</span>}
                  {tk.client_approval === "rejected" && <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 4, background: "rgba(239,68,68,0.15)", color: "#ef4444" }}>❌</span>}
                </div>
                <div style={{ display: "flex", gap: 8, fontSize: 11, color: t.textMuted }}>
                  {tk.project_name && <span style={{ color: "#6366f1", fontWeight: 600 }}>{tk.project_name}</span>}
                  <span>від: {getUserName(tk, "author")}</span>
                  <span>→ {getUserName(tk, "assignee") || "не призначено"}</span>
                  <span>{ago(tk.created_at)}</span>
                  {parseInt(tk.comment_count) > 0 && <span>💬 {tk.comment_count}</span>}
                </div>
              </div>
              <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: pr.color + "15", color: pr.color }}>{pr.label}</span>
              <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 8, background: st.bg, color: st.color }}>{st.label}</span>
            </div>
          );
        })}
        {filtered.length === 0 && <div style={{ textAlign: "center", padding: 30, color: t.textMuted }}>Немає тікетів</div>}
      </div>

      {/* Modal */}
      {openTicket && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setOpenTicket(null)}>
          <div onClick={e => e.stopPropagation()} style={{
            width: 640, maxHeight: "85vh", display: "flex", flexDirection: "column",
            background: dark ? "#1a1a24" : "#fff", borderRadius: 16, border: `1px solid ${t.border}`, boxShadow: "0 20px 60px rgba(0,0,0,.4)", overflow: "hidden",
          }}>
            <div style={{ padding: "18px 24px 12px", borderBottom: `1px solid ${t.border}`, flexShrink: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0, flex: 1 }}>{openTicket.title}</h3>
                <button onClick={() => setOpenTicket(null)} style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: t.textMuted }}>✕</button>
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <select value={openTicket.status} onChange={e => updateTicket(openTicket.id, { status: e.target.value })} style={{ ...inp, width: 120, fontSize: 11, padding: "3px 6px" }}>
                  {Object.entries(STATUS_MAP).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
                </select>
                <select value={openTicket.assignee_id || ""} onChange={e => updateTicket(openTicket.id, { assignee_id: e.target.value ? parseInt(e.target.value) : null })} style={{ ...inp, width: 150, fontSize: 11, padding: "3px 6px" }}>
                  <option value="">Виконавець</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.username.split("@")[0]}</option>)}
                </select>
                <select value={openTicket.priority} onChange={e => updateTicket(openTicket.id, { priority: e.target.value })} style={{ ...inp, width: 90, fontSize: 11, padding: "3px 6px" }}>
                  {Object.entries(PRIORITY_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
              {openTicket.description && <div style={{ padding: "12px 14px", borderRadius: 10, background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)", border: `1px solid ${dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"}`, fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-wrap", marginBottom: 16 }}>{openTicket.description}</div>}
              {openTicket.spec_url && <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 16, background: dark ? "rgba(99,102,241,0.06)" : "rgba(99,102,241,0.04)", border: `1px solid ${dark ? "rgba(99,102,241,0.15)" : "rgba(99,102,241,0.1)"}` }}><span style={{ fontSize: 11, fontWeight: 700, color: t.textMuted }}>📎 ТЗ: </span><a href={openTicket.spec_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: "#6366f1", textDecoration: "none" }}>{openTicket.spec_url}</a></div>}

              <div style={{ display: "flex", gap: 16, fontSize: 11, color: t.textMuted, marginBottom: 16, padding: "8px 12px", borderRadius: 8, background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)" }}>
                <span>Від: <strong style={{ color: t.text }}>{getUserName(openTicket, "author")}</strong></span>
                <span>Проєкт: <strong style={{ color: t.text }}>{openTicket.project_name || "—"}</strong></span>
                <span>{new Date(openTicket.created_at).toLocaleString("uk-UA")}</span>
              </div>

              <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>💬 Коментарі ({comments.length})</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
                {comments.map(c => (
                  <div key={c.id} style={{ padding: "10px 14px", borderRadius: 10, background: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)", border: `1px solid ${dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: c.client_name ? "#a855f7" : "#6366f1" }}>{c.client_name || c.author_full_name || c.author_name?.split("@")[0] || "—"}</span>
                      <span style={{ fontSize: 10, color: t.textMuted }}>{new Date(c.created_at).toLocaleString("uk-UA")}</span>
                    </div>
                    <div style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{c.text}</div>
                  </div>
                ))}
                <div ref={commentsEndRef} />
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <textarea value={commentText} onChange={e => setCommentText(e.target.value)} placeholder="Написати коментар..."
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); addComment(); } }}
                  rows={2} style={{ ...inp, flex: 1, fontSize: 12, padding: "8px 10px", resize: "none" }} />
                <button onClick={addComment} disabled={!commentText.trim()} style={{ ...btnP, fontSize: 11, padding: "8px 14px", alignSelf: "flex-end", opacity: commentText.trim() ? 1 : 0.5 }}>Надіслати</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
